/**
 * Stable device ID — persisted across sessions, **scoped per user**.
 *
 * Each user gets their own device ID on the same physical browser/device.
 * This prevents cross-user conflicts in Dexie Cloud: if user A registers
 * device ID X, user B on the same browser gets a different ID.
 *
 * Web: uses localStorage keyed by userId.
 * Native (Capacitor): uses NativeBridge plugin for Keychain (iOS) / AppSetId (Android).
 *   The native ID persists across reinstalls and is used for all users on the device.
 *
 * Used for device registration, force_logout detection, and analytics.
 */

import { Capacitor } from "@capacitor/core"
import { NativeBridge } from "@/shared/plugins/NativeBridge"

const STORAGE_PREFIX = "totodo_device_id:"
const LEGACY_KEY = "totodo_stable_device_id"

const cache = new Map<string, string>()

/**
 * Initialize the device ID from native bridge on Capacitor platforms.
 * Must be called once at app startup (before getStableDeviceId is called).
 * On web this is a no-op.
 */
export async function initNativeDeviceId(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const result = await NativeBridge.getStableDeviceId()

    if (result.stableDeviceId) {
      const key = `${STORAGE_PREFIX}${userId}`
      localStorage.setItem(key, result.stableDeviceId)
      cache.set(userId, result.stableDeviceId)
      console.log(
        `[stableDeviceId] Native ID initialized (source: ${result.source})`,
      )
    }
  } catch (e) {
    console.warn("[stableDeviceId] Failed to get native device ID:", e)
    // Fall through to localStorage-based ID
  }
}

/**
 * Returns a stable, persistent device ID for the given user.
 * Creates one on first call and stores it in localStorage.
 *
 * First user on a fresh device inherits the legacy device ID (if any)
 * to avoid orphaning an already-registered Dexie Cloud device record.
 */
export function getStableDeviceId(userId: string): string {
  const cached = cache.get(userId)
  if (cached) return cached

  const key = `${STORAGE_PREFIX}${userId}`
  let id = localStorage.getItem(key)

  if (!id) {
    // Migrate legacy key for the first user who claims it
    const legacyId = localStorage.getItem(LEGACY_KEY)
    if (legacyId && !localStorage.getItem(`${LEGACY_KEY}:claimed`)) {
      id = legacyId
      // Mark legacy ID as claimed so a second user won't reuse it
      localStorage.setItem(`${LEGACY_KEY}:claimed`, userId)
    } else {
      id = crypto.randomUUID()
    }
    localStorage.setItem(key, id)
  }

  cache.set(userId, id)
  return id
}

/** Clear the in-memory cache (call on logout). */
export function clearDeviceIdCache(): void {
  cache.clear()
}
